import React from "react";
import { Link } from "react-router-dom";
import { Stethoscope } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { mediaSrcSet, mediaVariant, resolveImage } from "@/lib/media";
import { rupees } from "@/lib/format";

const SIZE_CLASSES = {
  hero: "aspect-[5/4] w-full max-w-[300px]",
  rail: "aspect-[4/3] w-full",
  leaderboard: "aspect-[10/1] w-full",
  about: "aspect-square w-full max-w-[240px]",
};

/** L1-004 (owner decision, `03_triage.md`): when the RAIL slot specifically is vacant, it does not
 * fall back to the generic dashed ad placeholder — it shows an always-on house "Book a doctor"
 * service card (green-tinted, per the monetization City Home screenshot), so the rail is never a
 * dead ad-sales pitch to a citizen who just wants to find a doctor. It is never labelled
 * "Sponsored" (it isn't a sold slot) and links into the existing directory, pre-filtered to
 * appointment-taking businesses (`?filter=appointments`) rather than a category id, which is not
 * stable across tenants. Once the slot IS sold, the normal sold-creative rendering below takes
 * over exactly as it does for every other placement — this only touches the vacant branch. */
function RailHouseCard({ testId, canSellAds = false }) {
  const { t } = useApp();
  return (
    <div
      data-testid={testId ? `${testId}-house` : "ad-slot-house-rail"}
      className="flex flex-col gap-2 rounded-2xl border border-[hsl(var(--primary)/0.25)] bg-[hsl(var(--primary)/0.06)] p-4"
    >
      <span className="grid h-9 w-9 place-items-center rounded-full bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]">
        <Stethoscope size={17} aria-hidden />
      </span>
      <p className="font-heading text-sm font-semibold text-foreground">{t("cityAd.houseCard.title")}</p>
      <p className="text-xs text-muted-foreground">{t("cityAd.houseCard.body")}</p>
      <Link
        // Doctors only: the clinic category AND appointment-taking. Appointments alone also matched
        // salons (stylist slots reuse it) and any listing carrying the capability by mistake.
        to="/directory?category=hospital&filter=appointments"
        data-testid={testId ? `${testId}-house-cta` : "ad-slot-house-rail-cta"}
        className="mt-1 inline-flex items-center justify-center rounded-full bg-[hsl(var(--primary))] px-4 py-1.5 text-xs font-semibold text-primary-foreground"
      >
        {t("cityAd.houseCard.cta")}
      </Link>
      {/* The "advertise here" pitch is operator-facing sales copy, not for a citizen reading the
          public city home — only shown when the viewer can sell ad slots. */}
      {canSellAds && (
        <p className="text-[11px] text-muted-foreground">{t("cityAd.houseCard.advertise")}</p>
      )}
    </div>
  );
}

/** One shared renderer for all 4 operator-managed ad placements (hero, rail, leaderboard, about).
 * A sold slot shows the creative auto-labelled "Sponsored". A vacant slot renders the dashed
 * "Ad slot" placeholder with its weekly rate and a "Talk to the city desk" line (monetization
 * slice 5) when `showPlaceholder` is set — otherwise it renders nothing (legacy behaviour). That
 * placeholder, its rate and the rail house card's "advertise" line are operator-facing sales
 * prompts, so the public city home passes `showPlaceholder`/`canSellAds` only when the viewer can
 * sell ad slots (an operator/platform-admin) — a citizen never sees the internal rate-card text.
 * The vacant RAIL slot is the one exception (see `RailHouseCard` above): it shows the house card,
 * `showPlaceholder` or not — **but only when `houseCard` is true** (O-7, D-10). The card books a
 * doctor, so a city with no doctors must not offer one: the caller passes `houseCard` from the
 * hospital category's `listing_count`, and without it the rail behaves like every other vacant
 * slot (nothing for a citizen, the dashed placeholder for someone who can sell it). Opt-in by
 * design: a caller that cannot vouch for the city's doctors (the business storefront's `RailAd`)
 * gets no house card at all, which is also what that wrapper's own doc already promised. */
export function CityAdSlot({
  ad,
  slot,
  testId,
  rate,
  showPlaceholder = false,
  canSellAds = false,
  fallback = null,
  fallbackImage = "",
  houseCard = false,
}) {
  const { t, lang } = useApp();

  if (!ad) {
    if (slot === "rail" && houseCard) return <RailHouseCard testId={testId} canSellAds={canSellAds} />;
    // An unsold slot with something real to show (the city's own hero image) shows that, never the
    // ad pitch. Whoever can sell ads gets one friendly line beneath it.
    if (fallback) {
      return (
        <div className="flex flex-col gap-1.5" data-testid={testId ? `${testId}-fallback` : `ad-slot-fallback-${slot}`}>
          {fallback}
          {showPlaceholder && <p className="text-xs text-muted-foreground">{t("cityAd.talkToDesk")}</p>}
        </div>
      );
    }
    if (!showPlaceholder) return null;
    return (
      <div
        data-testid={testId ? `${testId}-vacant` : `ad-slot-vacant-${slot}`}
        className={`flex flex-col items-center justify-center gap-1 rounded-2xl border-[1.5px] border-dashed p-4 text-center ${SIZE_CLASSES[slot]}`}
        style={{ borderColor: "hsl(31 97% 52% / 0.45)", background: "hsl(31 97% 52% / 0.04)" }}
      >
        <span className="rounded-full bg-[hsl(31_97%_52%/0.12)] px-2 py-0.5 text-[11px] font-semibold text-[hsl(31_97%_32%)]">
          {t("cityAd.slotKicker", { name: t(`cityAd.slotName.${slot}`) })}
        </span>
        {rate != null && canSellAds && (
          <p className="font-mono text-[11px] text-muted-foreground">{t("cityAd.rate", { rate: rupees(rate) })}</p>
        )}
        <p className="text-xs text-muted-foreground">{t("cityAd.talkToDesk")}</p>
      </div>
    );
  }

  const title = localized(lang, ad, "title");
  const sponsor = localized(lang, ad, "sponsor_label");
  const isInternal = ad.link_url?.startsWith("/");

  // NEW10A-3: a SOLD slot whose advertiser supplied no creative shows the city's own hero picture
  // (when the caller has one) behind the sponsor caption, not a blank brand-colour box.
  const image = ad.image_url || fallbackImage || "";
  const content = (
    <>
      {image && (
        <img
          src={resolveImage(ad.image_url ? image : mediaVariant(image, "card"))}
          srcSet={ad.image_url ? undefined : mediaSrcSet(image) || undefined}
          sizes={ad.image_url ? undefined : "340px"}
          alt={title}
          // The hero slot is above the fold (eager); every other placement sits lower on the page.
          loading={slot === "hero" ? "eager" : "lazy"}
          decoding="async"
          className="h-full w-full object-cover"
        />
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
        <span className="rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
          {t("common.sponsored")}
        </span>
        <p className="mt-1 truncate text-sm font-semibold text-white">{title}</p>
        {sponsor && <p className="truncate text-xs text-white/80">{sponsor}</p>}
      </div>
    </>
  );

  // RF-12: a sold slot with no creative image was a light-grey box whose white caption text was
  // unreadable ("a blank grey card"). Without an image the card takes the city's brand colour so
  // the headline and sponsor line always have contrast.
  const surface = image ? "bg-muted" : "bg-[hsl(var(--primary))]";
  const className = `group relative block overflow-hidden rounded-2xl border border-border ${surface} transition-transform duration-200 hover:-translate-y-0.5 ${SIZE_CLASSES[slot]}`;

  if (!ad.link_url) {
    return (
      <div data-testid={testId} className={className}>
        {content}
      </div>
    );
  }

  if (isInternal) {
    return (
      <Link to={ad.link_url} data-testid={testId} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <a href={ad.link_url} target="_blank" rel="noopener noreferrer" data-testid={testId} className={className}>
      {content}
    </a>
  );
}
